/**
 * VitalsDrillDownPage Component
 * Placeholder page for vitals drill-down navigation
 * Shows the category and item being drilled into
 */

import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { WrenchScrewdriverIcon, ArrowLeftIcon } from '@heroicons/react/24/outline';
import { OathPageWrapper } from '../shared/OathUI';

// Map category/item keys to human-readable titles
const categoryTitles = {
  'snapshot': 'Current Snapshot',
  'accounts-receivable': 'Accounts Receivable',
  'aged-open-liability': 'Aged Open Liability',
};

const itemTitles = {
  // Snapshot items
  'open-liability': 'Open Liability',
  'open-judgments': 'Open Judgments',
  'open-forfeitures': 'Open Forfeitures',
  'last-report': 'Last Report',
  'turnaround': '6-Month Turnaround',
  'expired-powers': 'Expired Powers',
  'discharged-powers': 'Recent Discharges',
  'unexecuted-powers': 'Unexecuted Powers',
  'unexecuted-judgments': 'Unexecuted Judgments',
  'unexecuted-forfeitures': 'Unexecuted Forfeitures',
  'unexecuted-discharges': 'Unexecuted Discharges',
  'missing-powers': 'Missing Powers',
  // Accounts Receivable items
  'Current': 'Current',
  '30-60 Days': '30-60 Days',
  '60-90 Days': '60-90 Days',
  '90+ Days': '90+ Days',
  'Total': 'Total',
  // Aged Open Liability items
  '12-24 Months': '12-24 Months',
  '24-36 Months': '24-36 Months',
  '36+ Months': '36+ Months',
};

const VitalsDrillDownPage = () => {
  const { category, item } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const agencyId = searchParams.get('agencyId');

  // Decode URL parameters
  const decodedCategory = decodeURIComponent(category || '');
  const decodedItem = decodeURIComponent(item || '');

  // Get human-readable titles
  const categoryTitle = categoryTitles[decodedCategory] || decodedCategory;
  const itemTitle = itemTitles[decodedItem] || decodedItem;

  return (
    <OathPageWrapper maxWidth="max-w-lg">
      <div className="bg-white rounded-xl shadow-sm border border-primary-100 p-6 sm:p-8">
        <div className="text-center">
          {/* Icon */}
          <div className="w-20 h-20 rounded-full bg-primary flex items-center justify-center mx-auto mb-4">
            <WrenchScrewdriverIcon className="h-10 w-10 text-white" />
          </div>

          {/* Title */}
          <h1 className="text-xl font-semibold text-gray-900 mb-2">
            {itemTitle}
          </h1>

          {/* Breadcrumb */}
          <p className="text-sm text-primary mb-2">
            {categoryTitle}
          </p>

          {/* Description */}
          <p className="text-gray-500 mb-6">
            Detailed drill-down view for this metric will be available soon.
            {agencyId && (
              <span className="block text-xs text-gray-400 mt-2">
                Agency ID: {agencyId}
              </span>
            )}
          </p>

          {/* Back Button */}
          <button
            onClick={() => navigate('/app/dashboard')}
            className="inline-flex items-center gap-2 px-4 py-2 border border-primary text-primary rounded-md hover:bg-primary-50 transition-colors font-medium"
          >
            <ArrowLeftIcon className="h-4 w-4" />
            Back to Dashboard
          </button>
        </div>
      </div>
    </OathPageWrapper>
  );
};

export default VitalsDrillDownPage;
